// Shared player session helpers across all play modes (VS TEMPO / VS Robot /
// VS Humano). The codebase historically uses sessionStorage with these keys:
//   player_name, player_school, player_nationality, player_avatar_cfg,
//   player_level, training_user_id, vs_robot_name, vs_robot_school,
//   vs_robot_id, player_tier_id, train_profile (legacy JSON blob).
//
// We add this thin module to:
//   - expose `useUserProfile()` so chrome (Header/Footer) can react to login state
//   - expose `logoutSession()` that nukes every storage key + reloads `/`

import { useEffect, useState } from "react";

const SESSION_KEYS = [
  "player_name", "player_school", "player_nationality", "player_avatar_cfg",
  "player_level", "player_tier_id",
  "training_user_id",
  "vs_robot_name", "vs_robot_school", "vs_robot_id",
  "train_profile",
  "user_profile",
];

const LOCAL_KEYS = [
  "lang", "intro_shown", "muted",
  "user_profile",
  // Legacy training profile blob previously stored in localStorage.
  "train_profile", "train_name", "train_school",
];

const readName = () => {
  try {
    return (sessionStorage.getItem("player_name") || "").trim();
  } catch { return ""; }
};

export const useUserProfile = () => {
  const [name, setName] = useState(readName);

  // Poll storage so the chrome reacts after a route writes new player data.
  // (Browser storage events fire across tabs but not within the same one.)
  useEffect(() => {
    const tick = () => setName(readName());
    const id = setInterval(tick, 800);
    window.addEventListener("storage", tick);
    return () => { clearInterval(id); window.removeEventListener("storage", tick); };
  }, []);

  let school = "", nationality = "";
  try {
    school = sessionStorage.getItem("player_school") || "";
    nationality = sessionStorage.getItem("player_nationality") || "";
  } catch { /* noop */ }

  return {
    profile: { name, school, nationality },
    hasProfile: Boolean(name),
  };
};

// One-shot helper used by the Logout button — wipes all session+local storage
// related to the player and reloads the home page.
// When called mid-competition, `window.__supertActiveCompetition` is populated
// by GamePlay with { session_id, game_id, backend, time_seconds } so we can
// count the running attempt as a DEFEAT (status="lost") via sendBeacon before
// the redirect wipes everything. Without this the attempt would stay
// `in_progress` forever and `attempts_available` would not decrement.
export const logoutSession = () => {
  try {
    const info = typeof window !== "undefined" ? window.__supertActiveCompetition : null;
    if (info && info.session_id && info.game_id && info.backend) {
      const url = `${info.backend}/api/play/finish`;
      const payload = JSON.stringify({
        session_id: info.session_id,
        game_id: info.game_id,
        time_seconds: Number(info.time_seconds) || 0,
        status: "lost",
        client_submission_id: `logout-${info.session_id}`,
      });
      // sendBeacon survives the page navigation; fall back to keepalive fetch.
      let sent = false;
      try {
        const blob = new Blob([payload], { type: "application/json" });
        sent = navigator.sendBeacon(url, blob);
      } catch { /* ignore */ }
      if (!sent) {
        try {
          fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
          }).catch(() => { /* ignore */ });
        } catch { /* ignore */ }
      }
      try { delete window.__supertActiveCompetition; } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
  try { SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k)); } catch { /* noop */ }
  try { LOCAL_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* noop */ }
  window.location.href = "/";
};
